import { PROFILE } from "@/graphql/queries/profile";
import { useQuery } from "@apollo/client";
import { useTheme } from "@emotion/react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import ReportProblemRoundedIcon from "@mui/icons-material/NoAccounts";
import ReportIcon from "@mui/icons-material/ReportGmailerrorred";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import useGroups from "@/components/course/lesson/hooks/useGroups";

const donationBannerColors = {
    1: "#fea800",
    2: "#f3c0ad",
    3: "#f4ebe7",
    4: "#ab024e",
    5: "#3f982b",
    6: "#1a110a",
    7: "#05c0cc",
    8: "#a6a9b1",
    9: "#ef1419",
};

export default function TopBanner({ onClick }) {
    const router = useRouter();
    const lessonNumber = router?.query?.lessonNumber;

    const { userCurrentGroup } = useGroups({ courseId: 1 });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(600));

    const groupNumber = userCurrentGroup?.number;

    let bannerUrl = isMobile
        ? `/images/donation-banners/mobile/${groupNumber}.webp`
        : `/images/donation-banners/desktop/${groupNumber}.webp`;

    const { data: { profile } = {}, loading } = useQuery(PROFILE, {
        fetchPolicy: "cache-first",
    });

    const donationBannerShouldBeDisabled = lessonNumber <= 25;

    const isDropout = profile?.isDropout ?? false;

    const displayPasswordNotSet = profile?.hasPassword === false;

    if (isDropout) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    backgroundColor: "#FF0000",
                    padding: "6px 16px",
                }}
            >
                <ReportProblemRoundedIcon sx={{ fill: "#ffffff" }} />
                <Box width="12px" />
                <Typography
                    sx={{
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        paddingTop: "2px",
                    }}
                >
                    شما از دوره انصراف داده‌اید!
                </Typography>
            </Box>
        );
    }

    if (displayPasswordNotSet && router?.pathname !== '/settings') {
        return (
            <Box
                onClick={() => {router.push('/settings')}}
                sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    backgroundColor: "#FF0000",
                    padding: "6px 16px",
                    "@media(max-width: 800px)": {
                        flexDirection: "column",
                        alignItems: "start",
                        "button": {
                            marginLeft: "28px"
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center"
                    }}
                >
                    <ReportIcon sx={{ fill: "#ffffff" }} />
                    <Box width="12px" />
                    <Typography
                        sx={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "0.87rem",
                            paddingTop: "2px",
                        }}
                    >
                    جهت سهولت در ورود به سایت، رمز عبور خود را تنظیم بفرمایید.
                </Typography>
                </Box>
                <Box width="12px" />
                <Button 
                    variant="text" 
                    sx={{ color: "#fff", fontSize: "0.78rem", fontWeight: "bold" }} 
                    endIcon={<ChevronLeftIcon />}>
                    برو به تنظیمات
                </Button>
            </Box>
        )
    }

    const isProfileFullFilled = profile?.meta?.isFullFilled ?? false;

    if (
        router?.pathname !== "/courses/[courseId]/lessons/[lessonNumber]" ||
        loading ||
        donationBannerShouldBeDisabled ||
        !groupNumber
    ) {
        return <></>;
    }

    if (!isProfileFullFilled) {
        bannerUrl = isMobile
            ? "/images/profile-banner-mobile.webp"
            : "/images/profile-banner-desktop.webp";

        return (
            <Box
                sx={{
                    position: "relative",
                    height: { xs: "40px" },
                    width: "100%",
                    backgroundImage: `url(${bannerUrl})`,

                    backgroundRepeat: "no-repeat",
                    backgroundColor: "#fff5e2",
                    cursor: "pointer",
                    ...(isMobile
                        ? {
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                          }
                        : {
                              backgroundSize: "contain",
                              backgroundPosition: "center",
                          }),
                }}
                onClick={() => onClick("/profile")}
            />
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                height: { xs: "40px" },
                width: "100%",
                backgroundImage: `url(${bannerUrl})`,

                backgroundRepeat: "no-repeat",
                backgroundColor: donationBannerColors?.[groupNumber],
                cursor: "pointer",
                ...(isMobile
                    ? { backgroundSize: "cover", backgroundPosition: "center" }
                    : {
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                      }),
            }}
            onClick={() => onClick("/financial/donate")}
        />
    );
}
